/**
 * Chart.js set-up shared by every Dashboard chart, so axes, gridlines, lines
 * and tooltips behave the same everywhere. Chart.js is the library already in
 * the admin (react-chartjs-2); nothing else is added.
 *
 * Two rules every chart here follows:
 *   - Lines are straight between points (tension 0). A smoothed curve bends
 *     past its own data — above a peak, below $0 — and shows values that
 *     never happened.
 *   - The Y axis always runs from 0 to a "nice" ceiling above
 *     max(current, comparison) × 1.1, so no bar or line is ever clipped.
 */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js'
import { C, FONT } from './tokens'
import { moneyAxis } from './format'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Tooltip, Legend)
ChartJS.defaults.font.family = FONT
ChartJS.defaults.color = C.textMuted
ChartJS.defaults.elements.line.tension = 0

/**
 * 0 → a nice ceiling for `values`, with a round step so there are 4–5 ticks
 * ($0, $25, $50, $75…). Nulls (closed days) are ignored.
 */
export function niceScale(values, { money = true } = {}) {
  const nums = values.flat().map(Number).filter(v => Number.isFinite(v))
  const top = Math.max(0, ...nums) * 1.1
  if (top <= 0) return { max: money ? 100 : 4, step: money ? 25 : 1 }
  const rough = top / 4
  const mag = Math.pow(10, Math.floor(Math.log10(rough)))
  const candidates = money ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10]
  let step = mag * candidates.find(c => mag * c >= rough)
  if (!money) step = Math.max(1, Math.round(step))
  const max = Math.ceil(top / step) * step
  return { max, step }
}

/** Axes: light dashed horizontal gridlines, none vertical, fixed nice Y range. */
export function axes({ money = true, maxXTicks = 10, stacked = false, scale } = {}) {
  const s = scale || { max: undefined, step: undefined }
  return {
    x: {
      stacked,
      grid: { display: false, drawBorder: false },
      ticks: { color: C.textMuted, maxRotation: 0, autoSkip: true, maxTicksLimit: maxXTicks, font: { size: 11 } }
    },
    y: {
      stacked,
      min: 0,
      max: s.max,
      grid: { color: C.grid, borderDash: [4, 4], drawBorder: false, drawTicks: false },
      ticks: {
        color: C.textMuted,
        padding: 8,
        font: { size: 11 },
        stepSize: s.step,
        precision: money ? undefined : 0,
        callback: v => (money ? moneyAxis(v) : v)
      }
    }
  }
}

/* ── HTML tooltip: a white card, 8px radius, soft shadow ─────────────────── */

const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))

function tooltipEl(chart) {
  const host = chart.canvas.parentNode
  let el = host.querySelector(':scope > .rd-tooltip')
  if (!el) {
    el = document.createElement('div')
    el.className = 'rd-tooltip'
    el.setAttribute('role', 'presentation')
    Object.assign(el.style, {
      position: 'absolute',
      pointerEvents: 'none',
      background: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: '8px',
      boxShadow: '0 8px 24px rgba(17,24,39,0.12)',
      padding: '10px 12px',
      font: `12px ${FONT}`,
      color: C.text,
      fontVariantNumeric: 'tabular-nums',
      minWidth: '150px',
      whiteSpace: 'nowrap',
      transition: 'opacity 90ms ease',
      zIndex: 3
    })
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative'
    host.appendChild(el)
  }
  return el
}

/**
 * Tooltip config whose content comes from `build(index)`:
 *   { title, rows: [{ color, label, value, dashed, square }], delta: { text, good } | null, note }
 */
export function htmlTooltip(build) {
  return {
    enabled: false,
    external: ({ chart, tooltip }) => {
      const el = tooltipEl(chart)
      if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints || !tooltip.dataPoints.length) {
        el.style.opacity = 0
        return
      }
      const b = build(tooltip.dataPoints[0].dataIndex, tooltip)
      if (!b) { el.style.opacity = 0; return }
      const swatch = r => r.square
        ? `<span style="width:8px;height:8px;border-radius:2px;background:${r.color};box-shadow:inset 0 0 0 1px rgba(17,24,39,0.12);display:inline-block"></span>`
        : `<span style="width:12px;height:0;border-top:2px ${r.dashed ? 'dashed' : 'solid'} ${r.color};display:inline-block"></span>`
      const rows = (b.rows || []).map(r => `
        <div style="display:flex;align-items:center;gap:8px;margin-top:4px">
          ${swatch(r)}
          <span style="color:${C.textMuted}">${esc(r.label)}</span>
          <span style="margin-left:auto;padding-left:12px;font-weight:600;color:${C.text}">${esc(r.value)}</span>
        </div>`).join('')
      const delta = b.delta
        ? `<div style="margin-top:8px;padding-top:6px;border-top:1px solid ${C.border};font-weight:600;color:${b.delta.good == null ? C.textMuted : b.delta.good ? C.upText : C.downText}">${esc(b.delta.text)}</div>`
        : ''
      const note = b.note ? `<div style="margin-top:4px;color:${C.textMuted}">${esc(b.note)}</div>` : ''
      el.innerHTML = `<div style="font-weight:600;margin-bottom:2px">${esc(b.title)}</div>${rows}${delta}${note}`
      el.style.opacity = 1
      // Beside the point, flipped to the left near the right edge, kept inside the card.
      const hostW = chart.canvas.parentNode.clientWidth
      const w = el.offsetWidth
      let left = tooltip.caretX + 14
      if (left + w > hostW) left = tooltip.caretX - w - 14
      el.style.left = `${Math.max(0, left)}px`
      el.style.top = `${Math.max(0, Math.min(tooltip.caretY - el.offsetHeight / 2, chart.height - el.offsetHeight))}px`
    }
  }
}

export const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 250 },
  interaction: { mode: 'index', intersect: false },
  plugins: { legend: { display: false } }
}

/** Current series as a line: brand red, 2px, 4px dots. */
export function currentLine(data, { fill = false, dots = true } = {}) {
  return {
    label: 'This period',
    data,
    borderColor: C.brand,
    backgroundColor: fill ? C.brandFill : C.brand,
    borderWidth: 2,
    pointRadius: dots ? 4 : 0,
    pointHoverRadius: 5,
    pointBackgroundColor: C.brand,
    pointBorderColor: '#fff',
    pointBorderWidth: 1,
    tension: 0,
    fill: fill ? 'origin' : false,
    spanGaps: false,
    order: 1
  }
}

/** Comparison series as a line: gray, 1.5px dashed, no dots. */
export function comparisonLine(data) {
  return {
    label: 'Comparison',
    data,
    borderColor: C.prev,
    backgroundColor: C.prev,
    borderWidth: 1.5,
    borderDash: [5, 4],
    pointRadius: 0,
    pointHoverRadius: 3,
    tension: 0,
    fill: false,
    spanGaps: false,
    order: 2
  }
}

/* Grouped bars: a 1px transparent side border on each bar gives the 2px gap
   between the pair; only the top corners are rounded. */
const barShape = {
  borderColor: 'rgba(0,0,0,0)',
  borderWidth: { left: 1, right: 1, top: 0, bottom: 0 },
  borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
  borderSkipped: false,
  barPercentage: 0.9,
  categoryPercentage: 0.7,
  maxBarThickness: 28
}

export const currentBars = data => ({ label: 'This period', data, backgroundColor: C.brand, ...barShape, order: 1 })
export const comparisonBars = data => ({ label: 'Comparison', data, backgroundColor: C.prevBar, ...barShape, order: 2 })

/** True when there is nothing to draw — every value is zero or missing. */
export const allZero = (...series) => series.flat().every(v => v == null || Number(v) === 0)

/**
 * Writes a label above one point of dataset 0 — used for the peak hour.
 * Kept inside the chart area so it can never be cut off.
 */
export function pointLabelPlugin(getLabel) {
  return {
    id: 'rdPointLabel',
    afterDatasetsDraw(chart) {
      const lab = getLabel()
      if (!lab) return
      const meta = chart.getDatasetMeta(0)
      const pt = meta && meta.data && meta.data[lab.index]
      if (!pt) return
      const { ctx, chartArea } = chart
      ctx.save()
      ctx.font = `600 11px ${FONT}`
      const w = ctx.measureText(lab.text).width + 12
      const h = 20
      const x = Math.min(Math.max(pt.x - w / 2, chartArea.left), chartArea.right - w)
      const y = Math.max(pt.y - h - 8, chartArea.top)
      ctx.fillStyle = '#fff'
      ctx.strokeStyle = C.border
      ctx.lineWidth = 1
      if (ctx.roundRect) {
        ctx.beginPath(); ctx.roundRect(x, y, w, h, 6); ctx.fill(); ctx.stroke()
      } else {
        ctx.fillRect(x, y, w, h); ctx.strokeRect(x, y, w, h)
      }
      ctx.fillStyle = C.text
      ctx.textBaseline = 'middle'
      ctx.fillText(lab.text, x + 6, y + h / 2)
      ctx.restore()
    }
  }
}
